import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';

import { segmentClient } from '@/util/analytics';

type ChatAnalyticsContextValue = {
  inquiryId: string | number | undefined;
  conversationId: string | undefined;
  recipientId: string | number | undefined;
  recipientName: string | undefined;
  recipientUserType: string | undefined;
};

const ChatAnalyticsContext = createContext<ChatAnalyticsContextValue | null>(null);

type ChatAnalyticsProviderProps = ChatAnalyticsContextValue & {
  children: ReactNode;
};

/**
 * Provides chat-scoped analytics context (inquiry_id, conversation_id)
 * to all descendant components. Wrap at the chat screen level.
 */
export function ChatAnalyticsProvider({
  inquiryId,
  conversationId,
  recipientId,
  recipientName,
  recipientUserType,
  children,
}: ChatAnalyticsProviderProps) {
  const value = useMemo(
    () => ({ inquiryId, conversationId, recipientId, recipientName, recipientUserType }),
    [inquiryId, conversationId, recipientId, recipientName, recipientUserType]
  );

  return <ChatAnalyticsContext.Provider value={value}>{children}</ChatAnalyticsContext.Provider>;
}

/**
 * Returns a scoped `track` function that auto-injects `inquiry_id` and
 * `conversation_id` from the nearest `ChatAnalyticsProvider`.
 *
 * Falls back to plain `segmentClient.track` when used outside a provider.
 */
export function useChatAnalytics() {
  const ctx = useContext(ChatAnalyticsContext);

  const track: typeof segmentClient.track = useCallback(
    (event, properties) => {
      if (!ctx) {
        return segmentClient.track(event, properties);
      }

      return segmentClient.track(event, {
        ...(ctx.inquiryId != null && { inquiry_id: ctx.inquiryId }),
        ...(ctx.conversationId != null && { conversation_id: ctx.conversationId }),
        ...(ctx.recipientId != null && { recipient_id: ctx.recipientId }),
        ...(ctx.recipientName != null && { recipient_name: ctx.recipientName }),
        ...(ctx.recipientUserType != null && { recipient_user_type: ctx.recipientUserType }),
        ...(properties as Record<string, unknown>),
      });
    },
    [ctx]
  );

  return { track };
}
